import { offerBillingLabel } from '../../lib/offerBilling';
import type {
  PricebookDirectoryCountsViewModel,
  PricebookDirectoryQueryViewModel,
  PricebookDirectoryRowViewModel,
  PricebookEditorFormValue,
  PricebookMutationGroupsViewModel,
  PricebookPricingRecipe,
  PricebookProductGroupViewModel,
  PricebookReviewChangeGroupViewModel,
  PricebookReviewChangeItemViewModel,
  PricebookSelectedInspectorViewModel,
  PricebookValidationIssueViewModel,
} from '@workspace/silk-ui';
import type { Pricebook } from '@/lib/commerce';
import { parseCurrencyInputToMinor } from './currency';
import {
  serializeQuotePolicy,
  type PricebookQuotePolicyFormValue,
} from './quotePolicyController';

export const equipmentLeaseRecurringOfferType = 'equipment_lease_recurring';

export interface PortalPricebookEditorFormValue extends PricebookEditorFormValue {
  quotePolicy?: PricebookQuotePolicyFormValue;
}

export interface BuildPricebookDirectoryInput {
  pricebook: Pricebook;
  form: PricebookEditorFormValue;
  initialForm: PricebookEditorFormValue | null;
  validationErrors?: Record<string, string>;
  markedForRemovalRowIds?: string[];
  query: PricebookDirectoryQueryViewModel;
  expandedProductIds?: string[];
  selectedPriceRowId?: string | null;
}

export interface PricebookDirectoryResult {
  counts: PricebookDirectoryCountsViewModel;
  productGroups: PricebookProductGroupViewModel[];
  selectedInspector: PricebookSelectedInspectorViewModel | null;
}

export interface BuildPricebookMutationInput {
  form: PortalPricebookEditorFormValue;
  initialForm: PortalPricebookEditorFormValue | null;
  markedForRemovalRowIds?: string[];
}

export interface PricebookMutationResult {
  groups: PricebookMutationGroupsViewModel & { quotePolicyChanged?: true };
  payload: Record<string, unknown>;
}

export interface BuildPricebookReviewInput extends BuildPricebookMutationInput {
  validationIssues?: PricebookValidationIssueViewModel[];
  ignoredDraftRowIds?: string[];
  catalogLabels?: Record<string, string>;
}

type OfferRow = PricebookEditorFormValue['offerPrices'][number];
type FeeRow = PricebookEditorFormValue['feePrices'][number];
type OfferTarget = NonNullable<Pricebook['offer_targets']>[number];

const settingLabels: Record<string, string> = {
  name: 'Name',
  status: 'Lifecycle status',
  currency: 'Currency',
  catalogId: 'Catalog',
  sellingTenantId: 'Selling tenant',
  pricingStrategy: 'Pricing strategy',
  leaseTerms: 'Allowed lease terms',
  metadata: 'Advanced metadata',
};

const offerFieldLabels: Record<string, string> = {
  amount: 'Price',
  currency: 'Currency',
  leasePricing: 'Lease pricing',
  fees: 'Offer-specific charges',
  startsAt: 'Starts at',
  endsAt: 'Ends at',
};

const feeFieldLabels: Record<string, string> = {
  feeId: 'Catalog fee',
  chargeType: 'Charge type',
  amount: 'Amount',
  percentage: 'Percentage',
  currency: 'Currency',
  startsAt: 'Starts at',
  endsAt: 'Ends at',
};

function equal(left: unknown, right: unknown) {
  return JSON.stringify(left) === JSON.stringify(right);
}

function trimmed(value?: string | null) {
  return value?.trim() || '';
}

function toISOOrUndefined(value?: string | null): string | undefined {
  const valueTrimmed = value?.trim();
  if (!valueTrimmed) return undefined;
  const date = new Date(valueTrimmed);
  return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
}

function parseAllowedTerms(value: string): number[] {
  return Array.from(new Set(
    value
      .split(/[\s,]+/)
      .map(term => Number.parseInt(term, 10))
      .filter(term => Number.isInteger(term) && term > 0),
  )).sort((left, right) => left - right);
}

function metadataToRecord(entries: PricebookEditorFormValue['metadata']) {
  return Object.fromEntries(entries.flatMap(entry => {
    const key = entry.key.trim();
    if (!key) return [];
    if (entry.valueType === 'number') {
      const numberValue = Number(entry.value);
      return [[key, Number.isFinite(numberValue) ? numberValue : entry.value]];
    }
    if (entry.valueType === 'boolean') {
      return [[key, entry.value === 'true']];
    }
    if (entry.valueType === 'json') {
      try {
        return [[key, JSON.parse(entry.value)]];
      } catch {
        return [[key, entry.value]];
      }
    }
    return [[key, entry.value]];
  }));
}

function buildLeasePricingPayload(row: OfferRow) {
  if (row.offerType !== equipmentLeaseRecurringOfferType || !row.leasePricing) return undefined;
  const purchaseCurrency = trimmed(row.leasePricing.purchaseCostCurrency) || row.currency || 'USD';
  const saleCurrency = trimmed(row.leasePricing.intendedSalePriceCurrency) || row.currency || 'USD';
  return {
    purchase_cost_amount_minor: row.leasePricing.purchaseCostAmount == null
      ? undefined
      : parseCurrencyInputToMinor(String(row.leasePricing.purchaseCostAmount), purchaseCurrency, 'Purchase cost'),
    purchase_cost_currency: trimmed(row.leasePricing.purchaseCostCurrency) || undefined,
    intended_sale_price_amount_minor: row.leasePricing.intendedSalePriceAmount == null
      ? undefined
      : parseCurrencyInputToMinor(String(row.leasePricing.intendedSalePriceAmount), saleCurrency, 'Intended sale price'),
    intended_sale_price_currency: trimmed(row.leasePricing.intendedSalePriceCurrency) || undefined,
    lease_rate_bps: row.leasePricing.leaseRateBps ?? undefined,
    minimum_upfront_payment_amount_minor: row.leasePricing.minimumUpfrontPaymentAmount == null
      ? undefined
      : parseCurrencyInputToMinor(String(row.leasePricing.minimumUpfrontPaymentAmount), saleCurrency, 'Minimum upfront payment'),
    maximum_upfront_payment_amount_minor: row.leasePricing.maximumUpfrontPaymentAmount == null
      ? undefined
      : parseCurrencyInputToMinor(String(row.leasePricing.maximumUpfrontPaymentAmount), saleCurrency, 'Maximum upfront payment'),
  };
}

function buildOfferFeesPayload(row: OfferRow, fallbackCurrency?: string | null) {
  return (row.fees || []).map(fee => ({
    fee_label: fee.feeLabel.trim(),
    fee_code: trimmed(fee.feeCode) || undefined,
    charge_type: fee.chargeType,
    amount: trimmed(fee.amount) || undefined,
    percentage: trimmed(fee.percentage) || undefined,
    currency: trimmed(fee.currency) || trimmed(fallbackCurrency) || undefined,
    billing_period: trimmed(fee.billingPeriod) || undefined,
    starts_at: toISOOrUndefined(fee.startsAt),
    ends_at: toISOOrUndefined(fee.endsAt),
  }));
}

export function buildOfferPricePayload(row: OfferRow, fallbackCurrency?: string | null) {
  if (row.offerType === equipmentLeaseRecurringOfferType) {
    return {
      offer_id: row.offerId,
      lease_pricing: buildLeasePricingPayload(row),
      fees: buildOfferFeesPayload(row, fallbackCurrency),
      starts_at: toISOOrUndefined(row.startsAt),
      ends_at: toISOOrUndefined(row.endsAt),
    };
  }
  return {
    offer_id: row.offerId,
    amount: trimmed(row.amount) || undefined,
    currency: trimmed(row.currency) || trimmed(fallbackCurrency) || undefined,
    fees: buildOfferFeesPayload(row, fallbackCurrency),
    starts_at: toISOOrUndefined(row.startsAt),
    ends_at: toISOOrUndefined(row.endsAt),
  };
}

function buildFeePricePayload(row: FeeRow, fallbackCurrency?: string | null) {
  return {
    fee_id: row.feeId,
    charge_type: row.chargeType,
    amount: trimmed(row.amount) || undefined,
    percentage: trimmed(row.percentage) || undefined,
    currency: trimmed(row.currency) || trimmed(fallbackCurrency) || undefined,
    starts_at: toISOOrUndefined(row.startsAt),
    ends_at: toISOOrUndefined(row.endsAt),
  };
}

function offerRowsForMutation(form: PricebookEditorFormValue, removedRowIds: Set<string>) {
  return form.offerPrices
    .filter(row => !removedRowIds.has(row.rowId))
    .map(row => buildOfferPricePayload(row, form.currency));
}

function settingValue(form: PricebookEditorFormValue, field: string): unknown {
  switch (field) {
    case 'name': return form.name.trim();
    case 'status': return form.status;
    case 'currency': return trimmed(form.currency) || undefined;
    case 'catalogId': return trimmed(form.catalogId) || null;
    case 'sellingTenantId': return trimmed(form.sellingTenantId) || null;
    case 'pricingStrategy': return form.pricingStrategy;
    case 'leaseTerms': return parseAllowedTerms(form.leaseTerms.allowedTermMonthsText);
    case 'metadata': return metadataToRecord(form.metadata);
    default: return undefined;
  }
}

export function buildPricebookMutation(input: BuildPricebookMutationInput): PricebookMutationResult {
  const { form, initialForm } = input;
  const removedRowIds = new Set(input.markedForRemovalRowIds || []);
  const isCreate = initialForm === null;
  const shellFields = ['name', 'status', 'currency', 'catalogId', 'sellingTenantId', 'metadata']
    .filter(field => isCreate || !equal(settingValue(form, field), settingValue(initialForm, field)));
  const catalogScopeChanged = !isCreate && shellFields.includes('catalogId');
  const pricingStrategyChanged = isCreate || form.pricingStrategy !== initialForm.pricingStrategy;
  const leaseTermsChanged = isCreate || !equal(
    parseAllowedTerms(form.leaseTerms.allowedTermMonthsText),
    parseAllowedTerms(initialForm.leaseTerms.allowedTermMonthsText),
  );
  const currentOfferPrices = offerRowsForMutation(form, removedRowIds);
  const currentFeePrices = form.feePrices.map(row => buildFeePricePayload(row, form.currency));
  const initialOfferRowsById = new Map((initialForm?.offerPrices || []).map(row => [row.offerId, row]));
  const initialOfferRowsByRowId = new Map((initialForm?.offerPrices || []).map(row => [row.rowId, row]));
  const initialOfferRowIds = new Set((initialForm?.offerPrices || []).map(row => row.rowId));
  const currentOfferIds = new Set(form.offerPrices.map(row => row.offerId));
  const changedOfferRows = (catalogScopeChanged ? [] : form.offerPrices)
    .filter(row => !removedRowIds.has(row.rowId))
    .filter(row => {
      const initialRow = initialOfferRowsById.get(row.offerId);
      return !initialRow || !equal(
        buildOfferPricePayload(row),
        buildOfferPricePayload(initialRow),
      );
    });
  const changedOfferRowIds = changedOfferRows.map(row => row.rowId);
  const removedOfferRowIds = catalogScopeChanged
    ? Array.from(initialOfferRowIds)
    : Array.from(new Set([
        ...Array.from(removedRowIds).filter(rowId => initialOfferRowIds.has(rowId)),
        ...(initialForm?.offerPrices || [])
          .filter(row => !currentOfferIds.has(row.offerId))
          .map(row => row.rowId),
      ]));
  const removedOfferIds = Array.from(new Set(
    removedOfferRowIds.flatMap(rowId => {
      const offerId = initialOfferRowsByRowId.get(rowId)?.offerId;
      return offerId ? [offerId] : [];
    }),
  ));
  const initialFeeRowsById = new Map((initialForm?.feePrices || []).map(row => [row.feeId, row]));
  const currentFeeIds = new Set(form.feePrices.map(row => row.feeId));
  const changedFeeRowIds = form.feePrices
    .filter(row => {
      const initialRow = initialFeeRowsById.get(row.feeId);
      return !initialRow || !equal(
        buildFeePricePayload(row),
        buildFeePricePayload(initialRow),
      );
    })
    .map(row => row.rowId);
  const removedFeeRowIds = catalogScopeChanged
    ? (initialForm?.feePrices || []).map(row => row.rowId)
    : (initialForm?.feePrices || [])
        .filter(row => !currentFeeIds.has(row.feeId))
        .map(row => row.rowId);
  const offerPricesChanged = isCreate || changedOfferRowIds.length > 0 || removedOfferRowIds.length > 0;
  const feePricesChanged = isCreate || changedFeeRowIds.length > 0 || removedFeeRowIds.length > 0;
  const quotePolicy = serializeQuotePolicy(form.quotePolicy);
  const initialQuotePolicy = serializeQuotePolicy(initialForm?.quotePolicy);
  const quotePolicyChanged = isCreate ? quotePolicy !== undefined : !equal(quotePolicy, initialQuotePolicy);

  const payload: Record<string, unknown> = {};
  if (shellFields.includes('name')) payload.name = form.name.trim();
  if (shellFields.includes('status')) payload.status = form.status;
  if (shellFields.includes('currency')) payload.currency = trimmed(form.currency) || undefined;
  if (shellFields.includes('catalogId')) payload.catalog_id = trimmed(form.catalogId) || null;
  if (shellFields.includes('sellingTenantId') && (!isCreate || trimmed(form.sellingTenantId))) {
    payload.selling_tenant_id = trimmed(form.sellingTenantId) || null;
  }
  if (shellFields.includes('metadata')) payload.metadata = metadataToRecord(form.metadata);
  if (pricingStrategyChanged) payload.pricing_strategy = form.pricingStrategy;
  if (leaseTermsChanged) {
    payload.lease_terms = { allowed_term_months: parseAllowedTerms(form.leaseTerms.allowedTermMonthsText) };
  }
  if (quotePolicyChanged) payload.quote_calculation_policy = quotePolicy || null;
  if (isCreate) {
    payload.offer_prices = currentOfferPrices;
  } else if (catalogScopeChanged) {
    // A catalog transition cannot retain opaque rows from the previous scope.
    // Replace every semantic pricing collection atomically so the saved
    // pricebook can rehydrate targets from its newly selected catalog.
    payload.offer_prices = [];
    payload.variant_prices = [];
    payload.fee_prices = [];
  } else {
    if (changedOfferRows.length > 0) {
      payload.offer_price_upserts = changedOfferRows.map(row => buildOfferPricePayload(row, form.currency));
    }
    if (removedOfferIds.length > 0) payload.offer_price_removals = removedOfferIds;
  }
  if (feePricesChanged && !catalogScopeChanged) payload.fee_prices = currentFeePrices;

  return {
    groups: {
      shellFields,
      pricingStrategyChanged,
      leaseTermsChanged,
      offerPricesChanged,
      feePricesChanged,
      changedOfferRowIds,
      removedOfferRowIds,
      changedFeeRowIds,
      removedFeeRowIds,
      ...(quotePolicyChanged ? { quotePolicyChanged: true as const } : {}),
    },
    payload,
  };
}

export function offerPricingRecipe(offerType?: string | null): PricebookPricingRecipe {
  return offerType === equipmentLeaseRecurringOfferType ? 'lease' : 'amount';
}

export function offerTypeLabel(offerType?: string | null) {
  if (offerType === equipmentLeaseRecurringOfferType) return 'Lease';
  if (offerType === 'purchase') return 'Purchase';
  return 'Standard';
}

function syntheticProductId(label: string) {
  const slug = label.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
  return `historical:${slug || 'unknown-product'}`;
}

function targetIsCurrent(target: OfferTarget) {
  return target.status === 'active' && target.sellable !== false;
}

function formatCurrency(amount: number, currency: string) {
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(amount);
  } catch {
    return `${currency} ${amount.toFixed(2)}`;
  }
}

function currentPriceLabel(row: OfferRow | null, currency: string) {
  if (!row) return 'Not priced';
  if (row.offerType === equipmentLeaseRecurringOfferType) {
    const saleBasisValue = row.leasePricing?.intendedSalePriceAmount;
    const saleBasis = saleBasisValue == null || !saleBasisValue.trim() ? Number.NaN : Number(saleBasisValue);
    const rate = row.leasePricing?.leaseRateBps;
    const parts = [
      Number.isFinite(saleBasis)
        ? formatCurrency(saleBasis, row.leasePricing?.intendedSalePriceCurrency || currency)
        : null,
      rate != null ? `${(rate / 100).toFixed(2).replace(/\.00$/, '')}%` : null,
    ].filter(Boolean);
    return parts.length > 0 ? parts.join(' · ') : 'Lease pricing incomplete';
  }
  const amount = Number(row.amount);
  if (!trimmed(row.amount) || !Number.isFinite(amount)) return 'Price incomplete';
  return formatCurrency(amount, trimmed(row.currency) || currency);
}

function priceSortValue(row: PricebookDirectoryRowViewModel, formRows: Map<string, OfferRow>) {
  const formRow = formRows.get(row.offerId);
  if (!formRow) return Number.POSITIVE_INFINITY;
  if (formRow.offerType === equipmentLeaseRecurringOfferType) {
    const rawAmount = formRow.leasePricing?.intendedSalePriceAmount;
    const amount = rawAmount == null || !rawAmount.trim() ? Number.NaN : Number(rawAmount);
    return Number.isFinite(amount) ? amount : Number.POSITIVE_INFINITY;
  }
  const amount = Number(formRow.amount);
  return Number.isFinite(amount) ? amount : Number.POSITIVE_INFINITY;
}

function rowHasValidationError(index: number, validationErrors: Record<string, string>) {
  return Object.keys(validationErrors).some(field => field.startsWith(`offerPrices.${index}.`));
}

export function offerPriceRowNeedsAttention(row: OfferRow, pricebookCurrency: string) {
  if (!trimmed(row.offerId)) return true;
  if (row.offerType === equipmentLeaseRecurringOfferType) {
    const lease = row.leasePricing;
    const salePrice = Number(lease?.intendedSalePriceAmount);
    if (!lease?.purchaseCostAmount || Number(lease.purchaseCostAmount) <= 0) return true;
    if (!trimmed(lease.purchaseCostCurrency)) return true;
    if (!Number.isFinite(salePrice) || salePrice <= 0) return true;
    if (!trimmed(lease.intendedSalePriceCurrency)) return true;
    if (trimmed(lease.intendedSalePriceCurrency).toUpperCase() !== trimmed(pricebookCurrency).toUpperCase()) return true;
    if (lease.leaseRateBps == null || lease.leaseRateBps < 0) return true;
    if (lease.minimumUpfrontPaymentAmount != null && Number(lease.minimumUpfrontPaymentAmount) > salePrice) return true;
    if (lease.maximumUpfrontPaymentAmount != null && Number(lease.maximumUpfrontPaymentAmount) > salePrice) return true;
  } else if (!trimmed(row.amount)) {
    return true;
  }
  return (row.fees || []).some(fee => (
    !trimmed(fee.feeLabel)
    || (fee.chargeType === 'percentage' && !trimmed(fee.percentage))
    || (fee.chargeType !== 'percentage' && fee.chargeType !== 'included' && !trimmed(fee.amount))
    || (fee.chargeType === 'recurring' && !trimmed(fee.billingPeriod))
  ));
}

function lifecycleForRow(options: {
  row: OfferRow;
  initialRow?: OfferRow;
  hasValidationError: boolean;
  markedForRemoval: boolean;
}) {
  if (options.markedForRemoval) return 'marked_for_removal' as const;
  if (options.hasValidationError) return 'needs_attention' as const;
  if (!options.initialRow) return 'draft' as const;
  if (!equal(
    buildOfferPricePayload(options.row, options.row.currency),
    buildOfferPricePayload(options.initialRow, options.initialRow.currency),
  )) return 'modified' as const;
  return 'saved' as const;
}

function groupCounts(rows: PricebookDirectoryRowViewModel[]) {
  return {
    current: rows.filter(row => row.classification === 'current').length,
    priced: rows.filter(row => row.classification === 'current' && row.pricingPresence === 'priced').length,
    notInPricebook: rows.filter(row => row.classification === 'current' && row.pricingPresence === 'unpriced').length,
    needsAttention: rows.filter(row => row.classification === 'current' && row.lifecycle === 'needs_attention').length,
    historical: rows.filter(row => row.classification === 'historical').length,
  };
}

function rowMatchesView(row: PricebookDirectoryRowViewModel, view: PricebookDirectoryQueryViewModel['view']) {
  switch (view) {
    case 'priced': return row.classification === 'current' && row.pricingPresence === 'priced';
    case 'not_in_pricebook': return row.classification === 'current' && row.pricingPresence === 'unpriced';
    case 'needs_attention': return row.classification === 'current' && row.lifecycle === 'needs_attention';
    case 'historical': return row.classification === 'historical';
    case 'all_current': return row.classification === 'current';
  }
}

export function normalizePricebookDirectoryQuery(
  query: Omit<PricebookDirectoryQueryViewModel, 'activeFilterCount'> & { activeFilterCount?: number },
): PricebookDirectoryQueryViewModel {
  const productIds = Array.from(new Set(query.productIds));
  const offerTypes = Array.from(new Set(query.offerTypes));
  return {
    ...query,
    search: query.search,
    productIds,
    offerTypes,
    activeFilterCount: (query.search.trim() ? 1 : 0) + productIds.length + offerTypes.length,
  };
}

export function toggleExpandedProductId(expandedProductIds: string[], productId: string) {
  return expandedProductIds.includes(productId)
    ? expandedProductIds.filter(id => id !== productId)
    : [...expandedProductIds, productId];
}

export function buildDraftOfferPrice(options: {
  offerId: string;
  offerTargets?: Pricebook['offer_targets'];
  existingRows: OfferRow[];
  currency: string;
}): OfferRow | null {
  const existingOfferIds = new Set(options.existingRows.map(row => row.offerId));
  const targets = new Map((options.offerTargets || []).map(target => [target.offer_id, target]));
  if (existingOfferIds.has(options.offerId)) return null;
  const target = targets.get(options.offerId);
  if (!target || !targetIsCurrent(target)) return null;
  const recipe = offerPricingRecipe(target.offer_type);
  return {
      rowId: `draft-${target.offer_id}`,
      offerId: target.offer_id,
      productId: target.product_id,
      variantId: target.variant_id,
      productLabel: target.product_label,
      variantLabel: target.variant_label,
      offerLabel: target.offer_label,
      offerType: target.offer_type,
      termMonths: target.term_months ?? null,
      chargeType: target.billing_period_months ? 'recurring' : 'one_time',
      amount: '',
      percentage: '',
      currency: options.currency,
      billingPeriod: offerBillingLabel(target.billing_period_months),
      leasePricing: recipe === 'lease' ? {
        purchaseCostAmount: null,
        purchaseCostCurrency: options.currency,
        intendedSalePriceAmount: null,
        intendedSalePriceCurrency: options.currency,
        leaseRateBps: null,
        minimumUpfrontPaymentAmount: null,
        maximumUpfrontPaymentAmount: null,
      } : null,
      fees: [],
      summary: '',
  };
}

export function remapOfferPriceValidationAfterDiscard(
  validationErrors: Record<string, string>,
  removedIndex: number,
) {
  return Object.fromEntries(Object.entries(validationErrors).flatMap(([field, message]) => {
    const match = /^offerPrices\.(\d+)\.(.+)$/.exec(field);
    if (!match) return [[field, message]];
    const index = Number(match[1]);
    if (index === removedIndex) return [];
    if (index < removedIndex) return [[field, message]];
    return [[`offerPrices.${index - 1}.${match[2]}`, message]];
  }));
}

export function buildPricebookDirectory(input: BuildPricebookDirectoryInput): PricebookDirectoryResult {
  const validationErrors = input.validationErrors || {};
  const removedRowIds = new Set(input.markedForRemovalRowIds || []);
  const expandedIds = new Set(input.expandedProductIds || []);
  const activeTargets = (input.pricebook.offer_targets || []).filter(targetIsCurrent);
  const targetByOfferId = new Map(activeTargets.map(target => [target.offer_id, target]));
  const initialByOfferId = new Map((input.initialForm?.offerPrices || []).map(row => [row.offerId, row]));
  const formByOfferId = new Map(input.form.offerPrices.map(row => [row.offerId, row]));
  const rows: PricebookDirectoryRowViewModel[] = [];

  activeTargets.forEach(target => {
    const formRow = formByOfferId.get(target.offer_id) || null;
    const formIndex = formRow ? input.form.offerPrices.indexOf(formRow) : -1;
    const initialRow = initialByOfferId.get(target.offer_id);
    const hasValidationError = Boolean(formRow) && (
      (formIndex >= 0 && rowHasValidationError(formIndex, validationErrors))
      || (Boolean(initialRow) && offerPriceRowNeedsAttention(formRow, input.form.currency))
    );
    const lifecycle = formRow
      ? lifecycleForRow({
          row: formRow,
          initialRow,
          hasValidationError,
          markedForRemoval: removedRowIds.has(formRow.rowId),
        })
      : 'saved';
    const recipe = offerPricingRecipe(target.offer_type);
    const represented = Boolean(formRow);
    rows.push({
      rowId: formRow?.rowId || `target:${target.offer_id}`,
      offerId: target.offer_id,
      productId: target.product_id,
      variantId: target.variant_id,
      productLabel: target.product_label,
      variantLabel: target.variant_label,
      offerLabel: target.offer_label,
      offerType: target.offer_type,
      offerTypeLabel: offerTypeLabel(target.offer_type),
      recipe,
      classification: 'current',
      pricingPresence: represented ? 'priced' : 'unpriced',
      lifecycle,
      currentPriceLabel: currentPriceLabel(formRow, trimmed(input.form.currency) || 'USD'),
      offerSpecificChargeCount: formRow?.fees?.length || 0,
      hasValidationError,
      isSelected: input.selectedPriceRowId === formRow?.rowId,
    });
  });

  input.form.offerPrices.forEach((row, index) => {
    if (targetByOfferId.has(row.offerId)) return;
    const hasValidationError = rowHasValidationError(index, validationErrors);
    rows.push({
      rowId: row.rowId,
      offerId: row.offerId,
      productId: row.productId || syntheticProductId(row.productLabel),
      variantId: row.variantId,
      productLabel: row.productLabel || 'Historical product',
      variantLabel: row.variantLabel || 'Historical variant',
      offerLabel: row.offerLabel || 'Historical offer',
      offerType: row.offerType,
      offerTypeLabel: offerTypeLabel(row.offerType),
      recipe: offerPricingRecipe(row.offerType),
      classification: 'historical',
      pricingPresence: 'priced',
      lifecycle: lifecycleForRow({
        row,
        initialRow: initialByOfferId.get(row.offerId),
        hasValidationError,
        markedForRemoval: removedRowIds.has(row.rowId),
      }),
      currentPriceLabel: currentPriceLabel(row, trimmed(input.form.currency) || 'USD'),
      offerSpecificChargeCount: row.fees?.length || 0,
      hasValidationError,
      isSelected: input.selectedPriceRowId === row.rowId,
    });
  });

  const counts = {
    allCurrent: rows.filter(row => row.classification === 'current').length,
    priced: rows.filter(row => row.classification === 'current' && row.pricingPresence === 'priced').length,
    notInPricebook: rows.filter(row => row.classification === 'current' && row.pricingPresence === 'unpriced').length,
    needsAttention: rows.filter(row => row.classification === 'current' && row.lifecycle === 'needs_attention').length,
    historical: rows.filter(row => row.classification === 'historical').length,
  };

  const allGroups = new Map<string, { label: string; classification: 'current' | 'historical'; rows: PricebookDirectoryRowViewModel[] }>();
  rows.forEach(row => {
    const key = `${row.classification}:${row.productId}`;
    const group = allGroups.get(key) || { label: row.productLabel, classification: row.classification, rows: [] };
    group.rows.push(row);
    allGroups.set(key, group);
  });

  const search = input.query.search.trim().toLocaleLowerCase();
  const productIds = new Set(input.query.productIds);
  const offerTypes = new Set(input.query.offerTypes);
  const lifecycleRank = { needs_attention: 0, draft: 1, modified: 2, marked_for_removal: 3, saved: 4 } as const;
  const productGroups = Array.from(allGroups.entries()).flatMap(([key, group]) => {
    const productId = group.rows[0]?.productId || key;
    if (productIds.size > 0 && !productIds.has(productId)) return [];
    const productMatchesSearch = !search || group.label.toLocaleLowerCase().includes(search);
    const visibleRows = group.rows.filter(row => {
      if (!rowMatchesView(row, input.query.view)) return false;
      if (offerTypes.size > 0 && !offerTypes.has(row.offerType)) return false;
      if (productMatchesSearch) return true;
      return [row.variantLabel, row.offerLabel, row.offerTypeLabel]
        .some(label => label.toLocaleLowerCase().includes(search));
    });
    if (visibleRows.length === 0) return [];
    visibleRows.sort((left, right) => {
      if (input.query.sort === 'state') return lifecycleRank[left.lifecycle] - lifecycleRank[right.lifecycle];
      if (input.query.sort === 'price_asc' || input.query.sort === 'price_desc') {
        const direction = input.query.sort === 'price_asc' ? 1 : -1;
        return direction * (priceSortValue(left, formByOfferId) - priceSortValue(right, formByOfferId));
      }
      return `${left.variantLabel}\u0000${left.offerLabel}`.localeCompare(`${right.variantLabel}\u0000${right.offerLabel}`);
    });
    return [{
      productId,
      productLabel: group.label,
      classification: group.classification,
      counts: groupCounts(group.rows),
      isExpanded: expandedIds.has(productId) || Boolean(search),
      rows: visibleRows,
    } satisfies PricebookProductGroupViewModel];
  });

  productGroups.sort((left, right) => {
    const direction = input.query.sort === 'product_desc' ? -1 : 1;
    return direction * left.productLabel.localeCompare(right.productLabel);
  });

  const selectedRow = rows.find(row => row.rowId === input.selectedPriceRowId) || null;
  return {
    counts,
    productGroups,
    selectedInspector: selectedRow ? {
      rowId: selectedRow.rowId,
      productLabel: selectedRow.productLabel,
      variantLabel: selectedRow.variantLabel,
      offerLabel: selectedRow.offerLabel,
      offerTypeLabel: selectedRow.offerTypeLabel,
      classification: selectedRow.classification,
      lifecycle: selectedRow.lifecycle,
      currentPriceLabel: selectedRow.currentPriceLabel,
      offerSpecificChargeCount: selectedRow.offerSpecificChargeCount,
      isDraft: selectedRow.lifecycle === 'draft' || (
        selectedRow.classification === 'current'
        && !initialByOfferId.has(selectedRow.offerId)
      ),
      canEdit: selectedRow.classification === 'current' && selectedRow.pricingPresence === 'priced' && selectedRow.lifecycle !== 'marked_for_removal',
      canDiscardDraft: selectedRow.classification === 'current'
        && !initialByOfferId.has(selectedRow.offerId)
        && selectedRow.lifecycle !== 'marked_for_removal',
      canRemove: selectedRow.pricingPresence === 'priced'
        && initialByOfferId.has(selectedRow.offerId)
        && selectedRow.lifecycle !== 'marked_for_removal',
      canUndoRemoval: selectedRow.lifecycle === 'marked_for_removal',
    } : null,
  };
}

function displayValue(value: unknown) {
  if (value === null || value === undefined || value === '') return 'Not set';
  if (Array.isArray(value)) return value.length > 0 ? value.join(', ') : 'None';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

interface ChangedOfferField {
  field: string;
  label: string;
  beforeValue: string;
  afterValue: string;
}

const leaseReviewFields = [
  ['purchaseCostAmount', 'Purchase cost', 'money'],
  ['purchaseCostCurrency', 'Purchase cost currency', 'text'],
  ['intendedSalePriceAmount', 'Intended sale price', 'money'],
  ['intendedSalePriceCurrency', 'Intended sale price currency', 'text'],
  ['leaseRateBps', 'Interest rate', 'rate'],
  ['minimumUpfrontPaymentAmount', 'Minimum upfront payment', 'money'],
  ['maximumUpfrontPaymentAmount', 'Maximum upfront payment', 'money'],
] as const;

function leaseReviewValue(value: unknown, kind: 'money' | 'text' | 'rate', row: OfferRow) {
  if (value === null || value === undefined || value === '') return 'Not set';
  if (kind === 'rate') return `${(Number(value) / 100).toFixed(2).replace(/\.00$/, '')}%`;
  if (kind === 'money') {
    const currency = row.leasePricing?.intendedSalePriceCurrency || row.currency || 'USD';
    return formatCurrency(Number(value), currency);
  }
  return String(value);
}

function changedOfferFields(current: OfferRow, initial: OfferRow): ChangedOfferField[] {
  const currentPayload = buildOfferPricePayload(current, current.currency) as Record<string, unknown>;
  const initialPayload = buildOfferPricePayload(initial, initial.currency) as Record<string, unknown>;
  const fields = Object.keys(offerFieldLabels).filter(field => field !== 'leasePricing').flatMap(field => {
    const apiField = ({
      startsAt: 'starts_at',
      endsAt: 'ends_at',
    } as Record<string, string>)[field] || field;
    if (equal(currentPayload[apiField], initialPayload[apiField])) return [];
    return [{
      field,
      label: offerFieldLabels[field],
      beforeValue: displayValue((initial as unknown as Record<string, unknown>)[field]),
      afterValue: displayValue((current as unknown as Record<string, unknown>)[field]),
    }];
  });
  if (current.offerType !== equipmentLeaseRecurringOfferType && initial.offerType !== equipmentLeaseRecurringOfferType) return fields;
  leaseReviewFields.forEach(([field, label, kind]) => {
    const before = initial.leasePricing?.[field];
    const after = current.leasePricing?.[field];
    if (equal(before, after)) return;
    fields.push({
      field: `leasePricing.${field}`,
      label,
      beforeValue: leaseReviewValue(before, kind, initial),
      afterValue: leaseReviewValue(after, kind, current),
    });
  });
  return fields;
}

function changedFeeFields(current: FeeRow, initial: FeeRow) {
  const currentPayload = buildFeePricePayload(current, current.currency) as Record<string, unknown>;
  const initialPayload = buildFeePricePayload(initial, initial.currency) as Record<string, unknown>;
  return Object.keys(feeFieldLabels).filter(field => {
    const apiField = ({ startsAt: 'starts_at', endsAt: 'ends_at', feeId: 'fee_id', chargeType: 'charge_type' } as Record<string, string>)[field] || field;
    return !equal(currentPayload[apiField], initialPayload[apiField]);
  });
}

function changeGroup(
  id: PricebookReviewChangeGroupViewModel['id'],
  label: string,
  items: PricebookReviewChangeItemViewModel[],
): PricebookReviewChangeGroupViewModel {
  return { id, label, count: items.length, items };
}

export function buildPricebookReviewChangeGroups(input: BuildPricebookReviewInput): PricebookReviewChangeGroupViewModel[] {
  const initial = input.initialForm;
  const removedIds = new Set(input.markedForRemovalRowIds || []);
  const ignoredDraftIds = new Set(input.ignoredDraftRowIds || []);
  const settings: PricebookReviewChangeItemViewModel[] = [];
  const added: PricebookReviewChangeItemViewModel[] = [];
  const modified: PricebookReviewChangeItemViewModel[] = [];
  const removals: PricebookReviewChangeItemViewModel[] = [];
  const catalogFees: PricebookReviewChangeItemViewModel[] = [];

  ['name', 'status', 'currency', 'catalogId', 'sellingTenantId', 'pricingStrategy', 'leaseTerms', 'metadata'].forEach(field => {
    const before = initial ? settingValue(initial, field) : undefined;
    const after = settingValue(input.form, field);
    if (initial && equal(before, after)) return;
    if (!initial && field === 'metadata' && equal(after, {})) return;
    settings.push({
      id: `setting:${field}`,
      kind: 'setting',
      label: settingLabels[field],
      beforeValue: field === 'catalogId'
        ? (before ? input.catalogLabels?.[String(before)] || String(before) : 'All catalogs')
        : displayValue(before),
      afterValue: field === 'catalogId'
        ? (after ? input.catalogLabels?.[String(after)] || String(after) : 'All catalogs')
        : displayValue(after),
      destination: 'settings',
      field,
    });
  });

  const initialOffers = new Map((initial?.offerPrices || []).map(row => [row.offerId, row]));
  input.form.offerPrices.forEach(row => {
    if (ignoredDraftIds.has(row.rowId) && !initialOffers.has(row.offerId)) return;
    const contextLabel = [row.productLabel, row.variantLabel, row.offerLabel].filter(Boolean).join(' · ');
    if (removedIds.has(row.rowId)) {
      removals.push({
        id: `offer-remove:${row.rowId}`,
        kind: 'offer_removed',
        label: row.offerLabel || 'Offer price',
        contextLabel,
        destination: 'prices',
        scope: 'product_prices',
        rowId: row.rowId,
      });
      return;
    }
    const initialRow = initialOffers.get(row.offerId);
    if (!initialRow) {
      added.push({
        id: `offer-add:${row.rowId}`,
        kind: 'offer_added',
        label: row.offerLabel || 'Offer price',
        contextLabel,
        afterValue: currentPriceLabel(row, trimmed(input.form.currency) || 'USD'),
        destination: 'prices',
        scope: 'product_prices',
        rowId: row.rowId,
      });
      return;
    }
    changedOfferFields(row, initialRow).forEach(change => {
      modified.push({
        id: `offer-modify:${row.rowId}:${change.field}`,
        kind: 'offer_modified',
        label: `${row.offerLabel || 'Offer price'} — ${change.label}`,
        contextLabel,
        beforeValue: change.beforeValue,
        afterValue: change.afterValue,
        destination: 'prices',
        scope: 'product_prices',
        rowId: row.rowId,
        field: `offerPrices.${input.form.offerPrices.indexOf(row)}.${change.field}`,
      });
    });
  });

  const currentOfferIds = new Set(input.form.offerPrices.map(row => row.offerId));
  initial?.offerPrices.forEach(row => {
    if (!currentOfferIds.has(row.offerId) && !removedIds.has(row.rowId)) {
      removals.push({
        id: `offer-remove:${row.rowId}`,
        kind: 'offer_removed',
        label: row.offerLabel || 'Offer price',
        contextLabel: [row.productLabel, row.variantLabel, row.offerLabel].filter(Boolean).join(' · '),
        destination: 'prices',
        scope: 'product_prices',
        rowId: row.rowId,
      });
    }
  });

  const initialFees = new Map((initial?.feePrices || []).map(row => [row.feeId, row]));
  input.form.feePrices.forEach(row => {
    const initialRow = initialFees.get(row.feeId);
    if (!initialRow) {
      catalogFees.push({
        id: `fee-add:${row.rowId}`,
        kind: 'catalog_fee_added',
        label: row.feeLabel || 'Catalog fee',
        contextLabel: row.appliesToLabel,
        destination: 'prices',
        scope: 'catalog_fees',
        rowId: row.rowId,
      });
      return;
    }
    changedFeeFields(row, initialRow).forEach(field => {
      catalogFees.push({
        id: `fee-modify:${row.rowId}:${field}`,
        kind: 'catalog_fee_modified',
        label: `${row.feeLabel || 'Catalog fee'} — ${feeFieldLabels[field]}`,
        contextLabel: row.appliesToLabel,
        beforeValue: displayValue((initialRow as unknown as Record<string, unknown>)[field]),
        afterValue: displayValue((row as unknown as Record<string, unknown>)[field]),
        destination: 'prices',
        scope: 'catalog_fees',
        rowId: row.rowId,
        field: `feePrices.${input.form.feePrices.indexOf(row)}.${field}`,
      });
    });
  });
  const currentFeeIds = new Set(input.form.feePrices.map(row => row.feeId));
  initial?.feePrices.forEach(row => {
    if (!currentFeeIds.has(row.feeId)) {
      catalogFees.push({
        id: `fee-remove:${row.rowId}`,
        kind: 'catalog_fee_removed',
        label: row.feeLabel || 'Catalog fee',
        contextLabel: row.appliesToLabel,
        destination: 'prices',
        scope: 'catalog_fees',
        rowId: row.rowId,
      });
    }
  });

  const blockingIssues = (input.validationIssues || []).map(issue => ({
    id: `issue:${issue.id}`,
    kind: 'blocking_issue' as const,
    label: issue.message,
    contextLabel: [issue.productLabel, issue.variantLabel, issue.offerLabel].filter(Boolean).join(' · ') || undefined,
    destination: issue.destination,
    scope: issue.scope,
    rowId: issue.rowId,
    field: issue.field,
  }));

  return [
    changeGroup('settings', 'Settings changes', settings),
    changeGroup('added', 'Added prices', added),
    changeGroup('modified', 'Modified prices', modified),
    changeGroup('removals', 'Marked removals', removals),
    changeGroup('catalog_fees', 'Catalog fee changes', catalogFees),
    changeGroup('blocking_issues', 'Blocking issues', blockingIssues),
  ];
}

export function buildPricebookValidationIssues(
  form: PricebookEditorFormValue,
  validationErrors: Record<string, string>,
): PricebookValidationIssueViewModel[] {
  return Object.entries(validationErrors).map(([field, message]) => {
    const offerMatch = /^offerPrices\.(\d+)\./.exec(field);
    const feeMatch = /^feePrices\.(\d+)\./.exec(field);
    const offerRow = offerMatch ? form.offerPrices[Number(offerMatch[1])] : undefined;
    const feeRow = feeMatch ? form.feePrices[Number(feeMatch[1])] : undefined;
    return {
      id: field,
      destination: field.startsWith('quotePolicy.')
        ? 'quote_policy'
        : field === 'name' || field === 'catalogId' || field.startsWith('leaseTerms.')
          ? 'settings'
          : 'prices',
      scope: offerRow ? 'product_prices' : feeRow ? 'catalog_fees' : undefined,
      rowId: offerRow?.rowId || feeRow?.rowId || null,
      field,
      message,
      productLabel: offerRow?.productLabel,
      variantLabel: offerRow?.variantLabel,
      offerLabel: offerRow?.offerLabel || feeRow?.feeLabel,
    };
  });
}
